// src/components/Controls/Buttons/ButtonsContainer.js
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import SaveAsIcon from '@mui/icons-material/SaveAs';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';

import ConfirmDialog from '../Popups/ConfirmDialog';

const getRecordIdentifier = (values) => {
  return values?.uf ?? values?.id ?? null;
};

const ButtonsContainer = ({ values = {}, handleSave, handleDelete, disabled = false, hideDelete = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const xsMedia = useMediaQuery(theme.breakpoints.only('xs'));

  const [confirmDialog, setConfirmDialog] = React.useState({
    isOpen: false,
    title: '',
    subTitle: '',
    onConfirm: null,
  });

  const identifier = getRecordIdentifier(values);

  const isEmbeddedRecord = Boolean(values?.fieldName) && values?.index !== null && values?.index !== undefined;

  const canDelete = Boolean(identifier) || isEmbeddedRecord;

  const showSaveButton =
    typeof handleSave === 'function' &&
    (Object.prototype.hasOwnProperty.call(values, 'sn') || Object.prototype.hasOwnProperty.call(values, 'vn'));

  const closeConfirmDialog = () => {
    setConfirmDialog((previous) => ({
      ...previous,
      isOpen: false,
    }));
  };

  const openDeleteConfirmation = () => {
    if (typeof handleDelete !== 'function' || !canDelete) {
      return;
    }

    setConfirmDialog({
      isOpen: true,

      title: t('popup.are_you_sure_to_delete_record'),

      subTitle: t('popup.you_can_not_undo_this_operation'),

      onConfirm: async (event) => {
        try {
          /*
           * This keeps compatibility with:
           *
           * handleDelete(event, identifier)
           *
           * handleDelete(values)
           *
           * handleDelete()
           *
           * Extra JavaScript arguments are ignored
           * when the callback does not use them.
           */
          await handleDelete(event, identifier, values);
        } finally {
          closeConfirmDialog();
        }
      },
    });
  };

  return (
    <>
      <Stack direction={xsMedia ? 'column' : 'row'} spacing={2}>
        <Button type="submit" variant="outlined" startIcon={<SaveAsIcon />} disabled={disabled}>
          <Typography variant="button">{t('button.save_and_close')}</Typography>
        </Button>

        {showSaveButton && (
          <Button
            type="button"
            variant="outlined"
            startIcon={<SaveIcon />}
            disabled={disabled}
            onClick={(event) => handleSave(event, values)}
          >
            <Typography variant="button">{t('button.save')}</Typography>
          </Button>
        )}

        {!hideDelete && canDelete && (
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            startIcon={<DeleteIcon />}
            disabled={disabled}
            onClick={openDeleteConfirmation}
          >
            <Typography variant="button">{t('button.delete')}</Typography>
          </Button>
        )}
      </Stack>

      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </>
  );
};

export default ButtonsContainer;
